import React, { useMemo } from 'react';

import { Box, Flex, HStack, VStack } from '@chakra-ui/react';

import { Divider } from 'uiToolkitV1/Divider';
import { TextBodyMedium, TextLabel, TextHeader3, TextHeader1 } from 'uiToolkitV1/Typography';

import { formatCurrency } from './shared/stakeholderUtils';
import { useCompensationDrawersState } from './slice/compensationDrawersSlice';

export const TotalRaiseBudgetCard: React.FC<TotalRaiseBudgetCardProps> = () => {
  const { modifiedApproverWiseAllocationRows, modifiedCurrentPayDetailsRows } = useCompensationDrawersState();

  // extract org level percentage increase

  const { orgLevelPercentageIncrease, eligibleEmployeePay } = useMemo(() => {
    const orgLevelData = modifiedCurrentPayDetailsRows.find((row) => row?.function?.level === 'ORG');

    return {
      orgLevelPercentageIncrease: orgLevelData?.percentageIncrease,
      eligibleEmployeePay: orgLevelData?.currentPayroll,
    };
  }, [modifiedCurrentPayDetailsRows]);

  const additionalAdjustments = modifiedApproverWiseAllocationRows.reduce((acc, row) => acc + row.adjustedBudget, 0);

  const totalNewBudget =
    Number(eligibleEmployeePay) +
    Number(additionalAdjustments) +
    Number(Number(eligibleEmployeePay) * (Number(orgLevelPercentageIncrease) / 100));

  const effectiveIncrease = (
    ((totalNewBudget - Number(eligibleEmployeePay)) / Number(eligibleEmployeePay)) *
    100
  ).toFixed(2);

  const formatPercentage = (percentage: number) => `${percentage}%`;

  return (
    <Box
      display="flex"
      padding="8px 8px 12px 8px"
      flexDirection="column"
      alignItems="flex-start"
      gap="12px"
      alignSelf="stretch"
      borderRadius="8px"
      background="#FFF"
    >
      <Box width="100%">
        <Box display="flex" padding="8px 0px" alignItems="flex-start" gap="0px" alignSelf="stretch" borderRadius="0px">
          <TextBodyMedium bold>Total raise budget</TextBodyMedium>
        </Box>
        <Divider color="#EBEFF3" />
      </Box>

      <Box
        alignSelf="stretch"
        margin="0 8px"
        padding="8px"
        borderRadius="8px"
        border="1px solid #EBEFF3"
        background="linear-gradient(360deg, #FDFDFD 0%, rgba(245, 247, 249, 0.40) 100%)"
      >
        <Flex width="100%" height="100%" padding="16px 12px" alignItems="flex-start" gap="40px">
          {/* Left Section - Percentage */}
          <VStack align="flex-start" spacing="4px" minWidth="240px">
            <TextBodyMedium bold color="#666666">
              % increase/employee
            </TextBodyMedium>
            <TextHeader1 bold>{formatPercentage(Number(orgLevelPercentageIncrease))}</TextHeader1>
          </VStack>

          {/* Right Section - Calculation */}
          <Flex flex="1" alignItems="center" gap="16px" justifyContent="space-between">
            <Box color="#666666" fontSize="24px" fontWeight="normal">
              *
            </Box>
            {/* Eligible employee pay */}
            <VStack align="flex-start" spacing="4px">
              <TextBodyMedium bold color="#666666">
                Eligible employee pay
              </TextBodyMedium>
              <HStack spacing="8px" alignItems="center">
                <TextHeader3 bold>{formatCurrency(Number(eligibleEmployeePay))}</TextHeader3>
              </HStack>
            </VStack>

            <Box color="#666666" fontSize="24px" fontWeight="normal">
              +
            </Box>

            {/* Additional adjustments */}
            <VStack align="flex-start" spacing="4px">
              <TextBodyMedium bold color="#666666">
                Additional adjustments
              </TextBodyMedium>
              <TextHeader3 bold>{formatCurrency(additionalAdjustments)}</TextHeader3>
            </VStack>

            <Box color="#666666" fontSize="24px" fontWeight="normal">
              =
            </Box>

            {/* Total new budget */}
            <VStack align="flex-start" spacing="4px">
              <TextBodyMedium bold color="#666666">
                Total new budget
              </TextBodyMedium>
              <TextHeader3 bold>{formatCurrency(totalNewBudget)}</TextHeader3>
              <TextLabel color="#666666">Effective increase: {formatPercentage(Number(effectiveIncrease))}</TextLabel>
            </VStack>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};
